import React, { useEffect, useRef, useState } from 'react';
import {
  Loader2,
  AlertCircle,
  AlertTriangle,
  CreditCard,
  CheckCircle2,
  Zap,
  ImagePlus,
} from 'lucide-react';
import { REBILL_CHANGE_PLAN_URL } from '../config';
import { paymentServerFetch } from '../lib/paymentServer';
import { getPlanById, PLAN_UPGRADE_INCREMENTS } from '../lib/checkoutPlans';
import { getUserPlan } from '../lib/planUtils';

// Cambio de plan ONE-CLICK para clientes que YA están en Rebill (tienen tarjeta
// guardada). En vez del checkout embebido, muestra un botón "Confirmar cambio":
// el server cobra la tarjeta guardada (POST /rebill/change-plan, sin OTP) y el
// webhook `payment.created` completa la activación (créditos + baja de la sub
// vieja), igual que un pago del checkout.
//
// Respuestas del endpoint:
//  - { ok:true, subscriptionId, cardLast4?, cardBrand? } → cobrado → onSuccess.
//  - { ok:false, error:'needs_checkout' | 'no_saved_card' } → no se puede cobrar
//    en silencio → caemos al checkout embebido (onNeedsCheckout).
//  - { ok:false, error:'payment_declined' } → tarjeta rechazada → ofrecemos usar
//    otra tarjeta (→ checkout).
//  - { ok:false, error:'plan_not_in_rebill' } → plan sin mapear en el server →
//    error claro + fallback al checkout.
//  - { ok:false, error:'duplicate_request' } → ya hubo un cobro exitoso para esta
//    empresa hace <90s (con OTRA key) → probablemente salió: polleamos el plan.
//  - { ok:false, error:'idempotency_mismatch' } → la key se reusó con otro user o
//    plan. No cobra ni replica. Es un bug de scoping NUESTRO (ver más abajo).
//
// ─── Por qué hay idempotencia acá ───────────────────────────────────────────
// Sin `idempotency_key`, una respuesta PERDIDA (502 de Render, corte de red,
// timeout) era indistinguible de "no se cobró": mostrábamos "probá de nuevo" y
// cualquiera de las dos salidas de la UI (reintentar, o "pagar con otra tarjeta"
// → checkout embebido → suscripción nueva) volvía a cobrar. Con la key, el
// reintento le dice al server "esto ya lo mandé" y el server devuelve la
// respuesta del cobro original sin volver a cobrar.
//
// Regla que ordena todo lo de abajo: la lógica nueva corre SOLO en ramas que ya
// terminaban en un cartel de error. El camino del cobro exitoso quedó intacto a
// propósito — un bug acá degrada un error en otro error, no rompe lo que anda.
// Ver docs/superpowers/specs/2026-07-30-rebill-change-plan-idempotencia-design.md

/** Ventana de idempotencia del server (30 min). Nuestro TTL la espeja. */
const IDEMPOTENCY_TTL_MS = 30 * 60 * 1000;
/** El server corta a los 30s contra Rebill → una respuesta real siempre entra. */
const REQUEST_TIMEOUT_MS = 45_000;
/** Un 502 de Render arrancando en frío se cura en este rato; reintentar al
 *  instante pega contra la misma instancia dormida. */
const RETRY_DELAY_MS = 2_000;
/** Poll de activación, SOLO para `duplicate_request` (ver más abajo). */
const ACTIVATION_POLL_INTERVAL_MS = 2_000;
const ACTIVATION_POLL_TIMEOUT_MS = 15_000;

const WHATSAPP_SOPORTE_URL = `https://wa.me/5491122635392?text=${encodeURIComponent(
  'Buenas, intenté cambiar mi plan en Atlas y quedó sin confirmar. ¿Me ayudan a revisarlo?',
)}`;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ─── Idempotency key ────────────────────────────────────────────────────────
// Vive en `localStorage` y NO en `sessionStorage` porque sessionStorage es POR
// PESTAÑA: el cliente que reabre el checkout en una pestaña nueva arrancaría con
// una key nueva y el seguro no aplicaría. El TTL de 30 min acota la persistencia.
//
// Scopeada por (userId, plan): si el cliente vuelve atrás y elige otro plan, es
// otra intención de cobro y le toca otra key. Eso también evita el
// `idempotency_mismatch` del server (que valida la key contra user_id + plan).

const idemStorageKey = (userId: string, plan: string) =>
  `atlas:rebill:cp:${userId}:${plan}`;

function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  // Fallback para contextos no seguros / navegadores viejos. No necesita ser
  // criptográfico: solo único por intención de cobro.
  return `cp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Reusa la key vigente de esta intención (<30 min) o crea una nueva. */
function readOrCreateIdempotencyKey(userId: string, plan: string): string {
  const storageKey = idemStorageKey(userId, plan);
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw) as { key?: unknown; ts?: unknown };
      if (
        typeof parsed?.key === 'string' &&
        typeof parsed?.ts === 'number' &&
        Date.now() - parsed.ts < IDEMPOTENCY_TTL_MS
      ) {
        return parsed.key;
      }
    }
  } catch {
    // Storage bloqueado (Safari privado) o JSON corrupto → key nueva. Peor caso:
    // volvemos al comportamiento viejo, no rompemos nada.
  }
  const key = newIdempotencyKey();
  try {
    localStorage.setItem(storageKey, JSON.stringify({ key, ts: Date.now() }));
  } catch {
    // Sin storage la key vive solo para este intento (los reintentos de ESTA
    // llamada igual la comparten, que es donde más importa).
  }
  return key;
}

function clearIdempotencyKey(userId: string, plan: string): void {
  try {
    localStorage.removeItem(idemStorageKey(userId, plan));
  } catch {
    /* nada que limpiar */
  }
}

// ─── Telemetría ─────────────────────────────────────────────────────────────
// Antes el `catch` era pelado: un cambio de plan fallido no dejaba NINGÚN rastro
// del lado cliente. Sin datos de tarjeta ni del cliente — la traza completa (el
// error crudo de Rebill) vive en `payment_events` del payment server.
function logChangePlan(outcome: string, extra: Record<string, unknown> = {}) {
  console.error('[rebill/change-plan]', { outcome, ...extra });
}

// ─── Veredicto del server ───────────────────────────────────────────────────
type Outcome =
  /** Cobrado. */
  | { kind: 'charged'; subscriptionId?: string; cardLast4?: string }
  /** Definitivos SIN cobro: es seguro ofrecer el checkout embebido. */
  | { kind: 'declined' }
  | { kind: 'needs_checkout'; reason: string }
  | { kind: 'plan_unavailable' }
  | { kind: 'mismatch' }
  /** Hubo un cobro reciente con otra key → verificamos contra la DB. */
  | { kind: 'maybe_charged' }
  /** No sabemos si cobró. NUNCA ofrecer otra tarjeta acá. */
  | { kind: 'ambiguous' };

async function postChangePlan(
  userId: string,
  plan: string,
  idempotencyKey: string,
): Promise<Outcome> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    // paymentServerFetch inyecta el auth de Atlas + Content-Type solo.
    const res = await paymentServerFetch(REBILL_CHANGE_PLAN_URL, {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        plan,
        idempotency_key: idempotencyKey,
      }),
      signal: controller.signal,
    });
    // A propósito no miramos `res.ok`: el server manda los errores tipados con
    // status variado, y el código de `error` es más informativo que el status.
    // Un body no-JSON (502 de Render, HTML de error) cae en `ambiguous`.
    const data = (await res.json().catch(() => null)) as {
      ok?: boolean;
      subscriptionId?: string;
      error?: string;
      cardLastFour?: string;
      cardLast4?: string;
    } | null;

    if (data?.ok === true) {
      return {
        kind: 'charged',
        subscriptionId: data.subscriptionId,
        // `cardLast4` lo agregó el server recién; `cardLastFour` es defensivo.
        cardLast4: data.cardLast4 ?? data.cardLastFour,
      };
    }
    switch (data?.error) {
      case 'needs_checkout':
      case 'no_saved_card':
        return { kind: 'needs_checkout', reason: data.error };
      case 'payment_declined':
        return { kind: 'declined' };
      case 'plan_not_in_rebill':
        return { kind: 'plan_unavailable' };
      case 'duplicate_request':
        return { kind: 'maybe_charged' };
      case 'idempotency_mismatch':
        return { kind: 'mismatch' };
      default:
        return { kind: 'ambiguous' };
    }
  } catch {
    // Abort por timeout, corte de red, CORS, DNS. Puede haber cobrado.
    return { kind: 'ambiguous' };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Espera a que el webhook aplique el plan destino en `company_profiles`.
 * Mismo criterio que usa Checkout al volver de Stripe (`company.plan === planId`).
 *
 * OJO: esto corre SOLO en la rama `duplicate_request` — o sea, cuando el server
 * ya nos dijo que hubo un cobro exitoso reciente y necesitamos saber si es el
 * nuestro. El camino del cobro exitoso normal NO pollea.
 */
async function esperarPlanActivo(
  plan: string,
  sigueVivo: () => boolean,
): Promise<boolean> {
  const limite = Date.now() + ACTIVATION_POLL_TIMEOUT_MS;
  for (;;) {
    if (!sigueVivo()) return false;
    if ((await getUserPlan()) === plan) return true;
    if (Date.now() >= limite) return false;
    await sleep(ACTIVATION_POLL_INTERVAL_MS);
  }
}

export interface RebillChangePlanResult {
  subscriptionId?: string;
}

interface RebillChangePlanConfirmProps {
  /** UUID del profile (auth user id) que espera el endpoint como `user_id`. */
  userId: string;
  /** Plan de Atlas destino: 'emprendimiento' | 'negocio' | 'enterprise' | ... */
  plan: string;
  /** Precio formateado para mostrar en el botón (opcional). */
  priceLabel?: string;
  /** Últimos 4 dígitos de la tarjeta guardada, si los tenemos (para que el user
   * sepa a qué tarjeta se le cobra). Opcional: si no viene, no se muestra. */
  cardLast4?: string | null;
  /** Imágenes restantes actuales de la empresa. Si vienen, mostramos la suma
   * exacta (restantes + incremento del plan destino); si no, copy genérico. */
  currentImagesLeft?: number | null;
  onSuccess: (result: RebillChangePlanResult) => void;
  /** El front debe pasar a mostrar el checkout embebido (cargar tarjeta). */
  onNeedsCheckout: (reason: string) => void;
}

type Status =
  | 'idle'
  | 'loading'
  /** Consultando la DB tras un `duplicate_request`. */
  | 'verifying'
  | 'success'
  | 'declined'
  /** Error definitivo y SIN cobro → es seguro ofrecer otra tarjeta. */
  | 'error'
  /** No sabemos si cobró → NO ofrecer otra tarjeta. */
  | 'unconfirmed';

const RebillChangePlanConfirm: React.FC<RebillChangePlanConfirmProps> = ({
  userId,
  plan,
  priceLabel,
  cardLast4,
  currentImagesLeft,
  onSuccess,
  onNeedsCheckout,
}) => {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Terminación de la tarjeta con la que se cobró (del response, si viene).
  const [chargedLast4, setChargedLast4] = useState<string | null>(null);
  // El poll de activación puede seguir corriendo después de desmontar.
  const mountedRef = useRef(true);
  const enVueloRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Mecánica del cambio (igual para upgrade y downgrade): el webhook SUMA el
  // incremento del plan destino a las imágenes restantes; recién la próxima
  // renovación resetea al cupo mensual (`max_fotos_mes`) y cobra el precio
  // normal. Ver payment server, rebillWebhook (aplica a up y downgrades).
  const planInfo = getPlanById(plan);
  const planIncrement = PLAN_UPGRADE_INCREMENTS[plan] as number | undefined;
  const fmt = (n: number) => n.toLocaleString('es-AR');

  const marcarCobrado = (subscriptionId?: string, last4?: string | null) => {
    clearIdempotencyKey(userId, plan);
    setChargedLast4(last4 ?? cardLast4 ?? null);
    setStatus('success');
    onSuccess({ subscriptionId });
  };

  const ejecutarCambio = async () => {
    setStatus('loading');
    setErrorMsg(null);

    // La MISMA key para los dos intentos de esta intención de cobro.
    const idempotencyKey = readOrCreateIdempotencyKey(userId, plan);

    let outcome = await postChangePlan(userId, plan, idempotencyKey);

    // Respuesta ambigua: el server pudo haber cobrado. Reintentamos UNA vez con
    // la misma key — el mutex por empresa del server hace que este request espere
    // al original y devuelva su respuesta cacheada, sin cobrar de nuevo. Si el
    // cobro había salido, el cliente ve el éxito y nunca se entera del problema.
    if (outcome.kind === 'ambiguous') {
      logChangePlan('ambiguous', { attempt: 1, plan });
      await sleep(RETRY_DELAY_MS);
      if (!mountedRef.current) return;
      outcome = await postChangePlan(userId, plan, idempotencyKey);
    }
    if (!mountedRef.current) return;

    switch (outcome.kind) {
      case 'charged':
        marcarCobrado(outcome.subscriptionId, outcome.cardLast4);
        return;

      // Sin tarjeta cobrable / no es cliente Rebill → cargar tarjeta en el
      // checkout. No hubo cobro, así que es seguro mandarlo ahí.
      case 'needs_checkout':
        clearIdempotencyKey(userId, plan);
        logChangePlan(outcome.reason, { plan });
        onNeedsCheckout(outcome.reason);
        return;

      case 'declined':
        clearIdempotencyKey(userId, plan);
        logChangePlan('payment_declined', { plan });
        setStatus('declined');
        return;

      case 'plan_unavailable':
        clearIdempotencyKey(userId, plan);
        logChangePlan('plan_not_in_rebill', { plan });
        setStatus('error');
        setErrorMsg(
          'Este plan todavía no está disponible para el cambio directo.',
        );
        return;

      // La key se reusó con otro user/plan. El server no cobró ni replicó nada.
      // Con el scoping por (userId, plan) esto no debería pasar NUNCA: si
      // aparece en los logs, hay un bug de scoping del front.
      case 'mismatch':
        clearIdempotencyKey(userId, plan);
        logChangePlan('idempotency_mismatch', {
          plan,
          nota: 'bug de scoping del front: la key se reusó con otro user/plan',
        });
        setStatus('error');
        setErrorMsg('No pudimos procesar el cambio. Probá de nuevo.');
        return;

      // Hubo un cobro exitoso para esta empresa hace <90s, con OTRA key (p. ej.
      // el cliente confirmó desde otra pestaña, o un deploy del server perdió la
      // key in-memory). Probablemente ya salió → lo verificamos contra la DB en
      // vez de mostrarle un error a alguien a quien ya le cobramos.
      // La key NO se borra acá a propósito: si el poll no confirma, quedamos sin
      // saber si el cobro salió, y ahí conservarla es justo lo que hace que el
      // "Probar de nuevo" sea seguro. Solo la borra `marcarCobrado`.
      case 'maybe_charged': {
        logChangePlan('duplicate_request', { plan });
        setStatus('verifying');
        const activo = await esperarPlanActivo(plan, () => mountedRef.current);
        if (!mountedRef.current) return;
        if (activo) {
          marcarCobrado(undefined, null);
          return;
        }
        logChangePlan('duplicate_request_sin_activar', { plan });
        setStatus('unconfirmed');
        return;
      }

      // Dos respuestas ambiguas seguidas: no sabemos si cobró.
      case 'ambiguous':
        logChangePlan('ambiguous', { attempt: 2, plan });
        setStatus('unconfirmed');
        return;
    }
  };

  // Guarda contra el doble click: el botón se deshabilita al pasar a 'loading',
  // pero dos clicks en el mismo tick de React entrarían los dos igual. Compartirían
  // la misma key (o sea, el server los deduplicaría), pero no hace falta depender
  // de eso para algo que se resuelve con un ref.
  const confirm = async () => {
    if (enVueloRef.current) return;
    enVueloRef.current = true;
    try {
      await ejecutarCambio();
    } finally {
      enVueloRef.current = false;
    }
  };

  // Cambio confirmado → estado de éxito en el panel del form (el panel izquierdo
  // muestra la bienvenida). Evita que el botón quede en "Procesando…" post-cobro.
  if (status === 'success') {
    return (
      <div className="flex flex-col items-center text-center py-8">
        <div className="w-14 h-14 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-8 h-8 text-brand-primary" />
        </div>
        <p className="text-[17px] font-semibold text-brand-black/90">
          ¡Cambio de plan confirmado!
        </p>
        {chargedLast4 && (
          <p className="text-[14px] text-neutral-500 mt-1.5">
            Cobrado a tu tarjeta terminada en •• {chargedLast4}
          </p>
        )}
      </div>
    );
  }

  // Tarjeta rechazada → el cliente carga otra vía el checkout embebido. El server
  // confirmó que NO hubo cobro, así que ofrecer otra tarjeta es seguro acá.
  if (status === 'declined') {
    return (
      <div className="space-y-4">
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Tu tarjeta guardada fue rechazada. Probá con otra.</span>
        </div>
        <button
          type="button"
          onClick={() => onNeedsCheckout('payment_declined')}
          className="w-full py-3 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
        >
          Usar otra tarjeta
        </button>
      </div>
    );
  }

  // No sabemos si el cobro salió. Ámbar y no rojo a propósito: no es un fallo
  // confirmado, es un "no sé".
  //
  // Acá NO va "Pagar con otra tarjeta": ese botón manda al checkout embebido, que
  // crea una suscripción NUEVA — justo el camino al doble cobro, y justo en el
  // momento en que puede haber un cobro colgado. "Probar de nuevo" en cambio
  // reusa la misma idempotency key, así que el server devuelve el cobro original
  // en vez de hacer uno nuevo.
  if (status === 'unconfirmed') {
    return (
      <div className="space-y-4">
        <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2.5 text-[15px] text-amber-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            No pudimos confirmar si el cambio se completó. Es posible que el
            cobro ya se haya hecho, así que no cargues otra tarjeta.
          </span>
        </div>
        <button
          type="button"
          onClick={confirm}
          className="w-full py-3 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-xl shadow-sm transition-colors"
        >
          Probar de nuevo
        </button>
        <a
          href={WHATSAPP_SOPORTE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full text-center text-[14px] text-neutral-500 hover:text-neutral-700 font-medium transition-colors"
        >
          Escribinos y lo resolvemos
        </a>
      </div>
    );
  }

  const procesando = status === 'loading' || status === 'verifying';

  return (
    <div className="space-y-5">
      {/* Header con ícono */}
      <div className="flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-primary/15 to-brand-primary/5 flex items-center justify-center mb-3">
          <Zap className="w-7 h-7 text-brand-primary" />
        </div>
        <h2 className="text-[19px] font-semibold text-brand-black/90 tracking-tight">
          Confirmá tu cambio de plan
        </h2>
        <p className="text-[14px] text-neutral-500 mt-1.5 leading-relaxed max-w-[320px]">
          Al confirmar, hacemos el cobro al instante y empezás a usar tu nuevo
          plan ya mismo.
        </p>
      </div>

      {/* Puntos clave */}
      <div className="rounded-2xl border border-neutral-200/80 divide-y divide-neutral-100 overflow-hidden">
        <div className="flex items-start gap-3 p-3.5">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <p className="text-[14px] font-medium text-brand-black/90">
              Cobro inmediato
            </p>
            <p className="text-[13px] text-neutral-500 leading-snug">
              {cardLast4
                ? `Con tu tarjeta guardada terminada en •• ${cardLast4}, sin volver a cargarla.`
                : 'Con la tarjeta que ya tenés guardada, sin volver a cargarla.'}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-3.5">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
            <ImagePlus className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <p className="text-[14px] font-medium text-brand-black/90">
              No perdés lo que ya pagaste
            </p>
            <p className="text-[13px] text-neutral-500 leading-snug">
              {planIncrement != null && currentImagesLeft != null
                ? `Se sumarán ${fmt(planIncrement)} imágenes de este nuevo plan a las ${fmt(currentImagesLeft)} que te quedan: pasás a tener ${fmt(currentImagesLeft + planIncrement)} disponibles ya mismo.`
                : 'Las imágenes de este nuevo plan se suman a las que todavía te quedan.'}
              {planInfo
                ? ` A partir de la próxima fecha de renovación pasarás a tener ${fmt(planInfo.max_fotos_mes)} imágenes de forma mensual.`
                : ''}
            </p>
          </div>
        </div>
      </div>

      {status === 'error' && errorMsg && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <button
        type="button"
        onClick={confirm}
        disabled={procesando}
        className="w-full py-3 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {procesando ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />{' '}
            {status === 'verifying' ? 'Confirmando el estado…' : 'Procesando…'}
          </>
        ) : (
          `Confirmar cambio de plan${priceLabel ? ` · ${priceLabel}` : ''}`
        )}
      </button>

      {/* Solo en `error`, que son los veredictos definitivos SIN cobro. En
          `unconfirmed` esta salida no existe (ver el bloque de arriba). */}
      {status === 'error' && (
        <button
          type="button"
          onClick={() => onNeedsCheckout('error')}
          className="w-full text-[14px] text-neutral-500 hover:text-neutral-700 font-medium transition-colors"
        >
          Pagar con otra tarjeta
        </button>
      )}
    </div>
  );
};

export default RebillChangePlanConfirm;
